import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { apiPath, baseUrl } from 'config'
import {
  ApplicationRegistrationRequest,
  ApplicationRegistrationResponse,
  Fund,
  FundResponse,
  FundUpdateRequest,
} from 'types'

import { getSignature } from 'utils/getSignature'

import { setCurrentFund } from './slice'

export const queryFunds = createApi({
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    addWaitListRecord: builder.mutation<boolean, { amount: string; email: string; poolId: string }>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const { amount, email, poolId } = arg

        const payload = {
          amount,
          email,
          poolId,
        }

        const result = await baseQuery({
          body: payload,
          method: 'POST',
          url: apiPath.waitList,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as boolean }
      },
    }),
    createApplication: builder.mutation<ApplicationRegistrationResponse, ApplicationRegistrationRequest>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const { accountType, email, note, ownerName, ownerSurname, phone, referralCode, telegram } = arg

        const payload = {
          accountType,
          email,
          note,
          ownerName,
          ownerSurname,
          phone,
          referralCode,
          telegram,
        }

        const { deadline, signature } = await getSignature({
          payload,
          primaryType: 'CreateFundApplication',
          url: apiPath.application,
        })

        const result = await baseQuery({
          body: arg,
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url: apiPath.application,
        })
        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as ApplicationRegistrationResponse }
      },
    }),
    deployFundToChain: builder.mutation<{ success: boolean }, { chainId: number; fundId: string }>({
      invalidatesTags: ['GetFundById'],
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const { chainId, fundId } = arg
        const payload = { chainId, fundId }
        const url = apiPath.fundDeployChain

        const { deadline, signature } = await getSignature({
          payload,
          primaryType: 'DeployFundToChain',
          url,
        })

        const result = await baseQuery({
          body: payload,
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url,
        })

        if (result.error) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as { success: boolean } }
      },
    }),
    getFundByID: builder.query<Fund, string>({
      providesTags: ['GetFundById'],
      queryFn: async (fundID, _api, _extraOptions, baseQuery) => {
        const url = `${apiPath.fund}/${fundID}`

        const result = await baseQuery({
          url,
        })
        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        _api.dispatch(setCurrentFund(result.data as Fund))

        return { data: result.data as Fund }
      },
    }),
    getFunds: builder.query<FundResponse, void>({
      providesTags: ['GetFunds'],
      query: () => apiPath.fund,
    }),
    getFundsByAddress: builder.query<FundResponse, `0x${string}`>({
      query: (fundAddress) => `${apiPath.fund}?fundAddresses=${fundAddress}`,
    }),
    updateFund: builder.mutation<Fund, FundUpdateRequest>({
      invalidatesTags: ['GetFundById'],
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const { description, fundId, name } = arg

        const payload = {
          description,
          name,
        }

        const url = `${apiPath.fund}/${fundId}`

        const { deadline, signature } = await getSignature({
          payload: { ...payload, description: JSON.stringify(description) },
          primaryType: 'UpdateFund',
          url,
        })

        delete arg.fundId

        const result = await baseQuery({
          body: { ...arg, description: JSON.stringify(arg.description) },
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url,
        })
        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as Fund }
      },
    }),
  }),
  reducerPath: 'funds',
  tagTypes: ['GetFunds', 'GetFundById'],
})

export const {
  useAddWaitListRecordMutation,
  useCreateApplicationMutation,
  useDeployFundToChainMutation,
  useGetFundByIDQuery,
  useGetFundsByAddressQuery,
  useGetFundsQuery,
  useLazyGetFundsByAddressQuery,
  useLazyGetFundsQuery,
  useUpdateFundMutation,
} = queryFunds
